import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MessageCircle, CheckCircle2, AlertTriangle, UserCheck, BookMarked, Plus, Trash2, Sparkles, Users, MessageSquareText, Phone, Layers } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { questions } from '@/lib/api';

export default function QuestionsPage() {
  const me = JSON.parse(localStorage.getItem('immipro_user') || '{}');
  const [state, setState] = React.useState({ items: [], open_count: 0, sla_breached_count: 0, assigned_to_me: 0 });
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('all');
  const [assigneeFilter, setAssigneeFilter] = React.useState('all');
  const [reply, setReply] = React.useState({});
  const [busy, setBusy] = React.useState('');
  const [templates, setTemplates] = React.useState([]);
  const [rmas, setRmas] = React.useState([]);
  const [suggestions, setSuggestions] = React.useState({});
  const [templatePickerOpen, setTemplatePickerOpen] = React.useState({});
  const [newTemplate, setNewTemplate] = React.useState({ label: '', body: '', open: false });

  const load = React.useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter !== 'all') params.status_filter = filter;
    if (assigneeFilter !== 'all') params.assignee = assigneeFilter;
    questions.list(params).then(setState).finally(() => setLoading(false));
  }, [filter, assigneeFilter]);

  const loadAux = React.useCallback(() => {
    questions.templates.list().then((r) => setTemplates(r.items || [])).catch(() => {});
    questions.rmas().then((r) => setRmas(r.items || [])).catch(() => {});
  }, []);

  React.useEffect(load, [load]);
  React.useEffect(loadAux, [loadAux]);

  const insertTemplate = (qid, tpl) => {
    setReply((r) => ({ ...r, [qid]: `${r[qid] ? r[qid] + '\n\n' : ''}${tpl.body}` }));
    setTemplatePickerOpen((p) => ({ ...p, [qid]: false }));
    questions.templates.markUsed(tpl.id).catch(() => {});
  };

  const aiSuggest = async (qid) => {
    setSuggestions((s) => ({ ...s, [qid]: { loading: true } }));
    try {
      const r = await questions.suggestTemplate(qid);
      setSuggestions((s) => ({ ...s, [qid]: r }));
    } catch { setSuggestions((s) => ({ ...s, [qid]: null })); }
  };

  const reassign = async (qid, userId) => {
    await questions.assign(qid, userId || null);
    load();
    loadAux();
  };

  const saveTemplate = async () => {
    if (!newTemplate.label.trim() || !newTemplate.body.trim()) return;
    await questions.templates.create(newTemplate.label, newTemplate.body);
    setNewTemplate({ label: '', body: '', open: false });
    loadAux();
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm('Delete this reply template?')) return;
    await questions.templates.remove(tid);
    loadAux();
  };

  const [bulkOpen, setBulkOpen] = React.useState(null); // {matter_id, matter_ref, count}
  const [bulkReplyText, setBulkReplyText] = React.useState('');
  const [bulkSending, setBulkSending] = React.useState(false);
  const [bulkResult, setBulkResult] = React.useState(null);

  const bulkGroups = React.useMemo(() => {
    const map = new Map();
    (state.items || []).forEach((q) => {
      if (q.status !== 'open') return;
      const key = q.matter_id;
      if (!key) return;
      const g = map.get(key) || { matter_id: q.matter_id, matter_ref: q.matter_ref, client_email: q.client_email, count: 0, sla_breached: 0, questions: [] };
      g.count += 1;
      if (q.sla_breached) g.sla_breached += 1;
      g.questions.push(q);
      map.set(key, g);
    });
    return Array.from(map.values()).filter((g) => g.count >= 2).sort((a, b) => b.count - a.count);
  }, [state.items]);

  const openBulk = (group) => {
    setBulkOpen(group);
    setBulkReplyText('');
    setBulkResult(null);
  };

  const closeBulk = () => {
    setBulkOpen(null);
    setBulkReplyText('');
    setBulkResult(null);
  };

  const sendBulk = async () => {
    if (!bulkOpen || !bulkReplyText.trim()) return;
    setBulkSending(true);
    try {
      const r = await questions.bulkReply(bulkOpen.matter_id, bulkReplyText.trim());
      setBulkResult({ ok: true, replied: r.replied });
      load();
      setTimeout(() => closeBulk(), 1200);
    } catch (e) {
      setBulkResult({ ok: false, msg: e?.response?.data?.detail || 'Bulk reply failed' });
    } finally {
      setBulkSending(false);
    }
  };

  const insertBulkTemplate = (tpl) => {
    setBulkReplyText((t) => `${t ? t + '\n\n' : ''}${tpl.body}`);
    questions.templates.markUsed(tpl.id).catch(() => {});
  };

  const send = async (qid) => {
    const text = (reply[qid] || '').trim();
    if (!text) return;
    setBusy(qid);
    try {
      await questions.reply(qid, text, 'answered');
      setReply((r) => ({ ...r, [qid]: '' }));
      load();
    } finally { setBusy(''); }
  };

  const resolve = async (qid) => {
    setBusy(qid);
    try { await questions.setStatus(qid, 'resolved'); load(); }
    finally { setBusy(''); }
  };

  return (
    <div className="p-6" data-testid="questions-page">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#2ECC9A]/15">
          <MessageCircle className="h-4 w-4 text-[#2ECC9A]" />
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Client questions</h1>
          <div className="text-sm text-slate-500">
            {state.open_count} open
            {state.sla_breached_count > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700" data-testid="sla-summary-badge">
                <AlertTriangle className="h-3 w-3" /> {state.sla_breached_count} past 12h SLA
              </span>
            )}
            {' · replies land back on the client\'s portal + send an email notification.'}
          </div>
        </div>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger data-testid="assignee-filter" className="h-9 w-44 text-xs">
            <Users className="mr-1 h-3 w-3 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="me">Assigned to me{state.assigned_to_me > 0 ? ` (${state.assigned_to_me})` : ''}</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {rmas.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.name || r.email} ({r.open_load})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="questions-filter" className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="answered">Answered</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {bulkGroups.length > 0 && !loading && (
        <Card className="mt-6 border-[#2ECC9A]/30 bg-[#2ECC9A]/5" data-testid="bulk-reply-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <Layers className="h-4 w-4 text-[#2ECC9A]" /> Bulk reply — matters with multiple open threads
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              Answer once, close many. Each thread still keeps its own audit reply.
            </div>
            <div className="mt-3 space-y-1.5">
              {bulkGroups.map((g) => (
                <div
                  key={g.matter_id}
                  data-testid={`bulk-group-${g.matter_id}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white bg-white/70 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono font-semibold text-[#1A3A5C]">{g.matter_ref}</span>
                      <span className="text-slate-500">· {g.client_email}</span>
                      <Badge variant="outline" className="border-[#2ECC9A]/40 bg-white text-[10px] text-[#0F766E]">
                        {g.count} open
                      </Badge>
                      {g.sla_breached > 0 && (
                        <Badge variant="outline" className="border-red-300 bg-red-50 text-[10px] text-red-700">
                          <AlertTriangle className="mr-1 h-3 w-3" /> {g.sla_breached} past SLA
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    data-testid={`bulk-reply-open-${g.matter_id}`}
                    onClick={() => openBulk(g)}
                    size="sm"
                    className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs"
                  >
                    <MessageCircle className="h-3 w-3" /> Bulk reply
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="mt-8 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : state.items.length === 0 ? (
        <Card className="mt-6 border-dashed border-slate-200"><CardContent className="p-8 text-center text-xs text-slate-500">No questions in this filter.</CardContent></Card>
      ) : (
        <div className="mt-6 space-y-3" data-testid="questions-list">
          {state.items.map((q) => (
            <Card key={q.id} data-testid={`question-${q.id}`} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                      <span className="font-mono">{q.matter_ref}</span>
                      <span>·</span>
                      <span>{q.client_email}</span>
                      {q.visa && <><span>·</span><Badge variant="outline" className="border-sky-200 bg-sky-50 text-[10px] text-sky-700">visa {q.visa}</Badge></>}
                      <span>·</span>
                      <span>{new Date(q.created_at).toLocaleString('en-AU')}</span>
                      {q.channel && q.channel !== 'portal' && (
                        <Badge
                          data-testid={`channel-badge-${q.id}`}
                          variant="outline"
                          className={`text-[10px] ${
                            q.channel === 'whatsapp'
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-indigo-200 bg-indigo-50 text-indigo-700'
                          }`}
                          title={q.from_phone ? `From ${q.from_phone}` : ''}
                        >
                          {q.channel === 'whatsapp'
                            ? <><MessageSquareText className="mr-1 h-3 w-3" /> WhatsApp</>
                            : <><Phone className="mr-1 h-3 w-3" /> SMS</>}
                        </Badge>
                      )}
                      {q.sla_breached && (
                        <Badge
                          data-testid={`sla-badge-${q.id}`}
                          variant="outline"
                          className="border-red-300 bg-red-50 text-[10px] font-semibold text-red-700"
                          title="Client has been waiting more than 12 hours"
                        >
                          <AlertTriangle className="mr-1 h-3 w-3" /> Unanswered {Math.round(q.age_hours)}h
                        </Badge>
                      )}
                      {q.assigned_to_email && (
                        <Badge
                          data-testid={`assignee-${q.id}`}
                          variant="outline"
                          className="border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[10px] text-[#1A3A5C]"
                          title={q.assigned_reason === 'sla_auto' ? 'Auto-assigned to the least-loaded RMA (SLA breach)' : 'Assigned'}
                        >
                          <UserCheck className="mr-1 h-3 w-3" /> {q.assigned_to_name || q.assigned_to_email.split('@')[0]}
                          {q.assigned_reason === 'sla_auto' && <span className="ml-1 text-[9px] text-slate-500">· auto</span>}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-[#1A3A5C]">{q.text}</div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${
                    q.status === 'resolved' ? 'border-slate-200 bg-slate-50 text-slate-600'
                    : q.status === 'answered' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-amber-200 bg-amber-50 text-amber-700'
                  }`}>{q.status}</Badge>
                </div>

                {(q.replies || []).map((r) => (
                  <div key={r.id} className="mt-3 rounded-md bg-[#2ECC9A]/5 border border-[#2ECC9A]/20 p-2 text-xs">
                    <div className="text-[10px] text-slate-500">{r.author_name || r.author_email} · {new Date(r.created_at).toLocaleString('en-AU')}</div>
                    <div className="mt-1 whitespace-pre-line text-slate-700">{r.text}</div>
                  </div>
                ))}

                {q.status !== 'resolved' && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">Assign:</span>
                      <select
                        data-testid={`reassign-${q.id}`}
                        value={q.assigned_to || ''}
                        onChange={(e) => reassign(q.id, e.target.value)}
                        className="h-7 rounded-md border border-slate-200 bg-white px-2 text-[11px] text-slate-700 focus:border-[#2ECC9A] focus:outline-none"
                      >
                        <option value="">Unassigned</option>
                        {rmas.map((r) => (
                          <option key={r.id} value={r.id}>
                            {`${r.name || r.email} (${r.open_load})`}
                          </option>
                        ))}
                      </select>
                      <Button
                        data-testid={`ai-suggest-${q.id}`}
                        onClick={() => aiSuggest(q.id)}
                        disabled={suggestions[q.id]?.loading}
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[10px] text-[#1A3A5C] hover:bg-[#2ECC9A]/10"
                      >
                        {suggestions[q.id]?.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 text-[#2ECC9A]" />}
                        AI suggest template
                      </Button>
                      {suggestions[q.id] && !suggestions[q.id]?.loading && (
                        (() => {
                          const s = suggestions[q.id];
                          const tpl = s?.template_id ? templates.find((t) => t.id === s.template_id) : null;
                          if (tpl) {
                            return (
                              <button
                                data-testid={`ai-suggest-apply-${q.id}`}
                                onClick={() => insertTemplate(q.id, tpl)}
                                className="rounded-full border border-[#2ECC9A] bg-[#2ECC9A]/10 px-2 py-0.5 text-[10px] font-medium text-[#0F766E] hover:bg-[#2ECC9A]/20"
                                title={s.reason}
                              >
                                Apply “{tpl.label}”{typeof s.confidence === 'number' ? ` · ${Math.round(s.confidence * 100)}%` : ''}
                              </button>
                            );
                          }
                          return (
                            <span
                              data-testid={`ai-suggest-empty-${q.id}`}
                              className="text-[10px] italic text-slate-500"
                              title={s?.reason}
                            >
                              No confident template match
                            </span>
                          );
                        })()
                      )}
                    </div>
                    {templates.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1" data-testid={`templates-picker-${q.id}`}>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Templates:</span>
                        {templates.slice(0, 6).map((t) => (
                          <button
                            key={t.id}
                            data-testid={`template-insert-${t.id}-${q.id}`}
                            onClick={() => insertTemplate(q.id, t)}
                            className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600 hover:border-[#2ECC9A] hover:text-[#2ECC9A]"
                            title={t.body.slice(0, 200)}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <textarea
                      data-testid={`question-reply-${q.id}`}
                      value={reply[q.id] || ''}
                      onChange={(e) => setReply((r) => ({ ...r, [q.id]: e.target.value }))}
                      rows={2}
                      placeholder="Type your reply — or click a template above…"
                      className="w-full rounded-md border border-slate-200 bg-white p-2 text-xs"
                    />
                    <div className="flex items-center gap-2">
                      <Button data-testid={`question-send-${q.id}`} onClick={() => send(q.id)} disabled={busy === q.id || !(reply[q.id] || '').trim()} size="sm" className="h-8 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                        {busy === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send reply'}
                      </Button>
                      <Button data-testid={`question-resolve-${q.id}`} onClick={() => resolve(q.id)} disabled={busy === q.id} variant="outline" size="sm" className="h-8 gap-1 border-slate-200 text-xs">
                        <CheckCircle2 className="h-3 w-3" /> Mark resolved
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="mt-8 border-slate-200" data-testid="templates-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <BookMarked className="h-4 w-4 text-[#2ECC9A]" /> Reply templates ({templates.length})
            </div>
            <Button
              data-testid="template-new-btn"
              variant="outline"
              size="sm"
              onClick={() => setNewTemplate((t) => ({ ...t, open: !t.open }))}
              className="h-8 gap-1 border-slate-200 text-xs"
            >
              <Plus className="h-3 w-3" /> {newTemplate.open ? 'Cancel' : 'New template'}
            </Button>
          </div>
          {newTemplate.open && (
            <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-slate-50/50 p-3" data-testid="template-new-form">
              <input
                data-testid="template-new-label"
                value={newTemplate.label}
                onChange={(e) => setNewTemplate((t) => ({ ...t, label: e.target.value }))}
                placeholder="Template name (e.g. 'Send bank docs')"
                className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
              />
              <textarea
                data-testid="template-new-body"
                value={newTemplate.body}
                onChange={(e) => setNewTemplate((t) => ({ ...t, body: e.target.value }))}
                rows={3}
                placeholder="Template body (paste this reply as-is)…"
                className="w-full rounded-md border border-slate-200 bg-white p-2 text-xs"
              />
              <Button data-testid="template-new-save" onClick={saveTemplate} size="sm" className="h-8 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">Save template</Button>
            </div>
          )}
          {templates.length === 0 ? (
            <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
              No reply templates yet. Save common answers so you can insert them in one click.
            </div>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100" data-testid="templates-list">
              {templates.map((t) => (
                <li key={t.id} data-testid={`template-${t.id}`} className="flex items-start justify-between gap-2 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-[#1A3A5C]">{t.label}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500 line-clamp-2">{t.body}</div>
                    <div className="mt-0.5 text-[10px] text-slate-400">used {t.use_count || 0}×</div>
                  </div>
                  <button
                    data-testid={`template-delete-${t.id}`}
                    onClick={() => deleteTemplate(t.id)}
                    className="shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title="Delete template"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!bulkOpen} onOpenChange={(o) => { if (!o) closeBulk(); }}>
        <DialogContent data-testid="bulk-reply-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A3A5C]">
              <Layers className="h-4 w-4 text-[#2ECC9A]" />
              Bulk reply · {bulkOpen?.matter_ref}
            </DialogTitle>
          </DialogHeader>
          {bulkOpen && (
            <div className="space-y-3 text-xs">
              <div className="rounded-md bg-slate-50 p-3 text-[11px] text-slate-600">
                This reply will land on all <b className="text-[#1A3A5C]">{bulkOpen.count} open thread{bulkOpen.count === 1 ? '' : 's'}</b> on this matter, notify the client once per thread, and mark each thread as <b>Answered</b>.
              </div>
              {templates.length > 0 && (
                <div className="flex flex-wrap items-center gap-1" data-testid="bulk-templates-picker">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Templates:</span>
                  {templates.slice(0, 6).map((t) => (
                    <button
                      key={t.id}
                      data-testid={`bulk-template-insert-${t.id}`}
                      onClick={() => insertBulkTemplate(t)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-600 hover:border-[#2ECC9A] hover:text-[#2ECC9A]"
                      title={t.body.slice(0, 200)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                data-testid="bulk-reply-text"
                value={bulkReplyText}
                onChange={(e) => setBulkReplyText(e.target.value)}
                rows={5}
                placeholder="One reply that lands on every open thread on this matter…"
                className="w-full rounded-md border border-slate-200 bg-white p-2 text-xs"
              />
              <details className="rounded-md border border-slate-100">
                <summary className="cursor-pointer px-2 py-1 text-[10px] text-slate-500 hover:text-[#1A3A5C]">
                  Preview {bulkOpen.count} thread{bulkOpen.count === 1 ? '' : 's'}
                </summary>
                <ul className="max-h-32 space-y-1 overflow-auto px-2 py-1">
                  {bulkOpen.questions.map((q) => (
                    <li key={q.id} className="line-clamp-1 text-[11px] text-slate-600" title={q.text}>
                      · {q.text}
                    </li>
                  ))}
                </ul>
              </details>
              {bulkResult && (
                <div
                  data-testid="bulk-reply-result"
                  className={`rounded-md p-2 text-[11px] ${bulkResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                >
                  {bulkResult.ok
                    ? `Replied to ${bulkResult.replied} thread${bulkResult.replied === 1 ? '' : 's'}.`
                    : bulkResult.msg}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="bulk-reply-cancel"
              variant="outline"
              size="sm"
              onClick={closeBulk}
              disabled={bulkSending}
              className="h-8 text-xs"
            >
              Cancel
            </Button>
            <Button
              data-testid="bulk-reply-send"
              size="sm"
              onClick={sendBulk}
              disabled={bulkSending || !bulkReplyText.trim()}
              className="h-8 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs"
            >
              {bulkSending ? <Loader2 className="h-3 w-3 animate-spin" /> : `Send to ${bulkOpen?.count || 0} thread${bulkOpen?.count === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
